import InfoTagComponent from "@/components/info-tag/info-tag.component.vue";
import musclesViewComponent from "@/components/muscles-view/muscles-view.component.vue";
import WorkoutTileComponent from "@/components/workout-tile/workout-tile.component.vue";
import { ctrlCmdModule } from "@/store/modules/control-commands.module";
import { notificationModule } from "@/store/modules/notification.module";
import singleExerciseModule from "@/store/modules/single-exercise.module";
import { NotificationCommand, NotificationType, UserAnswer } from "@/types/notification";
import { PAGES } from "@/types/router";
import { listPageItem, singleExercise } from "@/types/singleExercises";
import { CTRL_COMMAND } from "@/types/socket";
import Vue, { WatchHandler } from "vue";
import { Component, Ref } from 'vue-property-decorator'

const itemsPerRow = 4

@Component({
    components: {
        'workout-tile-component': WorkoutTileComponent,
        'info-tag-component': InfoTagComponent,
        'muscle-view-component': musclesViewComponent
    }
})
export default class SingleExerciseLibrary extends Vue {

    private searchDebounce?: number
    private scrollDebounce?: number
    private notificationAnswerWatcher!: CallableFunction
    private scrollTop = 0
    public menuIsTriggered = false
    public searchPhrase = ''
    private idleWatcher!: CallableFunction
    private idleTimer!: number
    public currentPage = 1
    public totalExercises = 0
    @Ref() readonly scrollContainer!: any

    get hashMap() {
        return singleExerciseModule.hashMapItems
    }

    get activeExercise() {
        return singleExerciseModule.activeExercise
    }

    get exerciseList() {
        const filterList = singleExerciseModule.listItems
        let newList = [] as listPageItem[]

        if (this.selectedFilters.length > 0) {
            const idxArr: number[] = []
            this.selectedFilters.forEach((filter) => {
                const filteredSet = this.hashMap.get(filter.toLocaleLowerCase()) ?? []
                filteredSet.forEach((idx) => {
                    idxArr.push(idx)
                })
            })

            if(this.selectedFilters.length > 1) {
                const intersection: any = {}

                idxArr.forEach((idx) => intersection[idx] = (intersection[idx] || 0) + 1)
                Object.keys(intersection).forEach((idx: any) => {
                    if(intersection[idx] === this.selectedFilters.length) {
                    newList.push(filterList[idx] as any)
                    }
                })
            } else {
                idxArr.forEach((idx: number) => {
                    newList.push(filterList[idx])
                })
            }
        } else {
            newList = filterList
        }

        if (singleExerciseModule.searchPhraze) {
            newList = this.filterWorkoutsByName(newList, singleExerciseModule.searchPhraze)
        }
        this.totalExercises = newList.length
        return newList.slice(0, this.lazyRenderSlices)
    }

    get lazyRenderSlices() {
        return 16 * this.currentPage
    }

    get tileHeightPx() {
        return window.screen.availWidth <= 1440 ? 283.81 : 370
    }

    get isFiltersActive() { return false }

    get selectedIconClasses() {
        return ['selected', 'fontA-color-bg', 'primary-color-text']
    }

    get equipmentFilters() {
        return singleExerciseModule.filters.filter((filter: string) => filter.split('-')[0] === 'equipment')
    }

    get musculeFilters() {
        return singleExerciseModule.filters.filter((filter: string) => filter.split('-')[0] === 'muscleGroup')
    }

    get selectedFilters(): string[] {
        return singleExerciseModule.activeFilters
    }

    get selectedFilterClasses() {
        return ['primary-color-bg', 'selected']
    }

    mounted() {
        this.searchPhrase = singleExerciseModule.searchPhraze
        this.notificationAnswerWatcher = notificationModule.$watch(
            thisModule => thisModule.answer, this.userAnswerHandler)
        this.idleWatcher = singleExerciseModule.$watch(module => module.activeExercise, (activeExercise: string) => {
            if (activeExercise) {
                this.watchIdle()
            } else {
                if (this.idleTimer) window.clearTimeout(this.idleTimer)
                this.idleTimer = 0
            }
        })
    }

    watchIdle() {
        if (this.idleTimer) return
        this.idleTimer = window.setTimeout(() => {
            notificationModule.showNotification({ command: NotificationCommand.RESUME_EXERCISE, type: NotificationType.CONFIRM })
            this.idleTimer = window.setTimeout(() => {
                this.$router.push({ name: PAGES.HOME })
            }, 1 * 60 * 1000)
        }, 4 * 60 * 1000)
    }

    userAnswerHandler(answer: string) {
        if (notificationModule.command === NotificationCommand.RESUME_EXERCISE) {
            if (answer === UserAnswer.POSITIVE) {
                window.clearTimeout(this.idleTimer) 
                this.idleTimer = 0
                this.watchIdle()
            } else if (answer === UserAnswer.NEGATIVE) {
                this.$router.push({ name: PAGES.HOME })
            }
        }
    }

    removeFilterGroup(filter: string) {
        return filter.split('-')[1]
    }

    onScroll(e: any) {
        window.clearTimeout(this.scrollDebounce)
        this.scrollDebounce = window.setTimeout(() => {
            this.scrollTop = e.scrollTop
            this.currentPage++
        }, 400)
    }

    renderTile(tileIndex: number) {
        const from = Math.floor(this.scrollTop / this.tileHeightPx) * itemsPerRow
        const to = from + (itemsPerRow * 4)
        return tileIndex >= from && tileIndex < to
    }

    goToDescriptionPage(id: string) {
        const exercise = singleExerciseModule.exercises.find((exercise: singleExercise) => exercise.id === id)
        if (exercise) {
            const msg = {
                id: exercise.id,
                name: exercise.title_new,
                category: exercise.id.split('_')[0],
                tips: [exercise['en_step-1'], exercise['en_step-2'], exercise['en_step-3'], exercise['en_step-4']]
            }
            ctrlCmdModule.triggerCtrlCmd({trigger: CTRL_COMMAND.EXERCISE_START, value: msg})
        }
    }

    toggleFilterMenu(isOpen: boolean) {
        if (!this.menuIsTriggered) {
            this.menuIsTriggered = true
        }

        window.setTimeout(() => {
            this.menuIsTriggered = false
        }, 200)
    }

    toggleFilter(filter: string) {
        singleExerciseModule.addFilter(filter)
    }

    onChangeSearchPhrase(newPhrase: string) {
        window.clearTimeout(this.searchDebounce)
        this.searchDebounce = window.setTimeout(() => {
            singleExerciseModule.search(newPhrase)
            this.scrollContainer.scrollTo({y: 0})
        }, 700)
    }

    filterWorkoutsByName(workouts: listPageItem[], searchPhraze: string) {
        if (searchPhraze) {
            return workouts.filter(workout => {
                return workout.name.toLowerCase().includes(searchPhraze.toLowerCase())
            })
        }
        return workouts
    }

    clearAll() {
        singleExerciseModule.clearAll()
        this.searchPhrase = ''
    }

    destroyed() {
        if (this.idleWatcher) this.idleWatcher()
        if (this.idleTimer) window.clearTimeout(this.idleTimer)
        if (this.notificationAnswerWatcher) this.notificationAnswerWatcher()
        notificationModule.hideNotification()
        this.clearAll()
        this.toggleFilterMenu(false)
        ctrlCmdModule.triggerCtrlCmd({trigger: CTRL_COMMAND.EXERCISE_STOP, value: {id:''}})
    }
}